import React from "react";
import { Box, Divider, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";

import { ReactComponent as EditIcon } from "assets/images/icons/btn_edit.svg";
import { ReactComponent as RemoveIcon } from "assets/images/icons/icn_btn_bin.svg";
import { ReactComponent as RemoveHoverIcon } from "assets/images/icons/icn_btn_bin_hover.svg";

const ActionIcons = ({
  rowData,
  editEnabled,
  removeEnabled,
  onEdit,
  onRemove,
}) => {
  const [isRemoveHovered, setIsRemoveHovered] = React.useState(false);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      <EditIcon
        onClick={() => {
          if (editEnabled) onEdit?.(rowData);
        }}
        style={{
          cursor: editEnabled ? "pointer" : "default",
          margin: "0 6px 0 2px",
          width: "18px",
          height: "18px",
          opacity: editEnabled ? 1 : 0.35,
        }}
      />
      {removeEnabled && (
        <>
          <Divider
            orientation="vertical"
            flexItem
            sx={{
              borderColor: "primary.greyThread",
              height: "20px",
              width: "1px",
              margin: "0 10px",
            }}
          />
          <Box
            onMouseEnter={() => setIsRemoveHovered(true)}
            onMouseLeave={() => setIsRemoveHovered(false)}
            onClick={() => onRemove?.(rowData)}
            sx={{
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
            }}
          >
            {isRemoveHovered ? (
              <RemoveHoverIcon style={{ width: "20px", height: "20px", margin: "0 6px 0 2px" }} />
            ) : (
              <RemoveIcon style={{ width: "20px", height: "20px", margin: "0 6px 0 2px" }} />
            )}
          </Box>
        </>
      )}
    </Box>
  );
};

const EllipsisTooltipCell = ({ text, maxWidth = 220 }) => {
  const theme = useTheme();
  const textRef = React.useRef(null);
  const [isOverflow, setIsOverflow] = React.useState(false);
  const displayText = text || "";

  React.useEffect(() => {
    const el = textRef.current;
    if (!el) return;

    setIsOverflow(el.scrollWidth > el.clientWidth);
  }, [displayText]);

  return (
    <Tooltip
      arrow
      disableHoverListener={!isOverflow}
      title={isOverflow ? (
        <Typography
          sx={{
            color: theme.palette.primary.white,
            fontSize: theme.typography.customFontSize[1],
            lineHeight: 1.54,
            whiteSpace: "pre-line",
          }}
        >
          {displayText}
        </Typography>
      ) : ""}
      slotProps={{
        tooltip: {
          sx: {
            maxWidth: 600,
          },
        },
      }}
    >
      <Box
        ref={textRef}
        sx={{
          maxWidth,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          cursor: isOverflow ? "pointer" : "default",
        }}
      >
        {displayText}
      </Box>
    </Tooltip>
  );
};

export const getTableColumns = ({
  columnHelper,
  showEditGeneral,
  disabled = false,
  onEdit,
  onRemove,
}) => [
  columnHelper.accessor("policyLabel", {
    header: "Policy",
    size: 90,
    meta: {},
    cell: (info) => <Box>{info.getValue()}</Box>,
  }),
  columnHelper.accessor("protocolLabel", {
    header: "Protocol",
    size: 110,
    meta: {},
    cell: (info) => <Box>{info.getValue()}</Box>,
  }),
  columnHelper.accessor("sourceLabel", {
    header: "Source",
    size: 150,
    meta: {},
    cell: (info) => <Box>{info.getValue()}</Box>,
  }),
  columnHelper.accessor("srcPortLabel", {
    header: "Src Port",
    size: 100,
    meta: {},
    cell: (info) => <Box>{info.getValue()}</Box>,
  }),
  columnHelper.accessor("destinationLabel", {
    header: "Destination",
    size: 150,
    meta: {},
    cell: (info) => <Box>{info.getValue()}</Box>,
  }),
  columnHelper.accessor("dstPortLabel", {
    header: "Dst Port",
    size: 100,
    meta: {},
    cell: (info) => <Box>{info.getValue()}</Box>,
  }),
  columnHelper.accessor("comment", {
    header: "Description",
    size: 220,
    meta: {},
    cell: (info) => <EllipsisTooltipCell text={info.getValue()} />,
  }),
  columnHelper.display({
    id: "action",
    header: "Action",
    size: 90,
    meta: { isAction: true },
    cell: (info) => {
      const rule = info.row.original.ref;

      return (
        <ActionIcons
          rowData={rule}
          editEnabled={!disabled}
          removeEnabled={showEditGeneral && !disabled}
          onEdit={onEdit}
          onRemove={onRemove}
        />
      );
    },
  }),
];
